import React, { useState, useEffect } from 'react';
import { Modal, Alert, Spinner } from 'react-bootstrap';
import { getUNits } from "../../api/unit";
import { getProductByProductIdAndFamer, updateSTock } from '../../api/products';

const EditProductModal = ({ show, handleClose, product }) => {
  const [unitData, setUnitData] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    unit: '',
    productID: '',
  });
  const [traderProducts, setTraderProducts] = useState([]);
  const [editedTraders, setEditedTraders] = useState({});
  const [loadingTraders, setLoadingTraders] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ show: false, type: '', message: '' });
  const [removingTrader, setRemovingTrader] = useState(null);

  function getUnitLists() {
    getUNits((result) => {
      if (result.ok === 1) {
        setUnitData(result.result);
      } else {
        showFeedback('danger', 'Failed to load units. Please check your internet connection.');
      }
    });
  }

  const fetchTraderProducts = (productId) => {
    if (!productId) return;
    
    setLoadingTraders(true);
    getProductByProductIdAndFamer(productId, (result) => {
      setLoadingTraders(false);
      if (result.ok === 1) {
        setTraderProducts(result.result);
        const initialEdits = {};
        result.result.forEach((trader, idx) => {
          initialEdits[idx] = {
            quantity: trader.quantity || 0,
            selling_price: trader.selling_price || 0,
            changed: false
          };
        });
        setEditedTraders(initialEdits);
      } else {
        showFeedback('warning', 'Failed to fetch trader products.');
        setTraderProducts([]);
      }
    });
  };

  const showFeedback = (type, message) => {
    setFeedback({ show: true, type, message });
    setTimeout(() => {
      setFeedback({ show: false, type: '', message: '' });
    }, 4000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTraderInputChange = (index, field, value) => {
    setEditedTraders(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value,
        changed: true
      }
    }));
  };

  const handleUpdateTrader = async (index) => {
    const trader = traderProducts[index];
    const editedData = editedTraders[index];

    if (!editedData.changed) {
      showFeedback('info', 'No changes detected for this trader.');
      return;
    }

    if (editedData.quantity <= 0) {
      showFeedback('danger', 'Quantity must be greater than 0');
      return;
    }
    if (editedData.selling_price <= 0) {
      showFeedback('danger', 'Price must be greater than 0');
      return;
    }

    const updateData = {
      trader_id: trader.ownerId || trader.ownerId,
      product_id: formData.productID,
      trader_name: trader.full_name,
      quantity: editedData.quantity,
      selling_price: editedData.selling_price,
      currency: trader.currency_name || 'TSH',
      previous_quantity: trader.quantity,
      previous_price: trader.selling_price,
      timestamp: new Date().toISOString()
    };

       

          updateSTock(updateData, (result)=>{
                setLoadingTraders(false);
                if (result.ok === 1) {
                  showFeedback('success', `Updated ${trader.full_name}'s product successfully!`);
                }
                else {
                  showFeedback('danger', `Failed to update ${trader.full_name}'s product. Please try again.`);
                }
          })

    try {
      setSaving(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      showFeedback('success', `Updated ${trader.full_name}'s product successfully!`);
      
      setEditedTraders(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          changed: false
        }
      }));
      
      fetchTraderProducts(formData.productID);
      
    } catch (error) {
      console.error('Update Trader Error:', error);
      showFeedback('danger', 'Failed to update trader. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveTrader = async (index) => {
    const trader = traderProducts[index];
    
    if (!window.confirm(`Are you sure you want to remove ${trader.full_name}?`)) {
      return;
    }

    const removeData = {
      trader_id: trader.id || trader.trader_id,
      product_id: formData.productID,
      trader_name: trader.full_name,
      quantity_removed: trader.quantity,
      price_removed: trader.selling_price,
      currency: trader.currency_name || 'TSH',
      timestamp: new Date().toISOString()
    };

    console.log('Remove Trader Data:', JSON.stringify(removeData, null, 2));

    try {
      setRemovingTrader(index);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      showFeedback('success', `${trader.full_name} removed successfully!`);
      fetchTraderProducts(formData.productID);
      
    } catch (error) {
      console.error('Remove Trader Error:', error);
      showFeedback('danger', 'Failed to remove trader. Please try again.');
    } finally {
      setRemovingTrader(null);
    }
  };

  useEffect(() => {
    getUnitLists();
    
    if (product) {
      const initialData = {
        name: product.name || '',
        unit: product.unit || '',
        productID: product.productID || product.id || ''
      };
      setFormData(initialData);
      
      if (initialData.productID) {
        fetchTraderProducts(initialData.productID);
      }
    }
  }, [product]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showFeedback('danger', 'Product name is required');
      return;
    }
    if (!formData.unit) {
      showFeedback('danger', 'Please select a unit');
      return;
    }

    const tradersData = traderProducts.map((trader, index) => ({
      trader_id: trader.id || trader.trader_id,
      trader_name: trader.full_name,
      quantity: editedTraders[index]?.quantity ?? trader.quantity,
      selling_price: editedTraders[index]?.selling_price ?? trader.selling_price,
      currency: trader.currency_name || 'TSH',
      is_modified: editedTraders[index]?.changed || false,
      original_quantity: trader.quantity,
      original_price: trader.selling_price
    }));

    const submissionData = {
      product: {
        product_id: formData.productID,
        name: formData.name,
        unit: formData.unit,
        original_name: product.name,
        original_unit: product.unit
      },
      traders: tradersData,
      modified_traders: tradersData.filter(t => t.is_modified),
      total_traders: tradersData.length,
      modified_count: tradersData.filter(t => t.is_modified).length,
      timestamp: new Date().toISOString(),
      user_action: 'UPDATE_PRODUCT'
    };

    console.log('Final Product Submission Data:', JSON.stringify(submissionData, null, 2));

    try {
      setSaving(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      showFeedback('success', 'Product updated successfully!');
      
      setTimeout(() => {
        handleClose();
      }, 1500);
      
    } catch (error) {
      console.error('Submit Error:', error);
      showFeedback('danger', 'Failed to update product. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!product) return null;

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold text-dark">
          <i className="bi bi-pencil-square me-2 text-primary"></i>
          Edit Product
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {/* Feedback Alert */}
        {feedback.show && (
          <Alert 
            variant={feedback.type} 
            dismissible 
            onClose={() => setFeedback({ show: false, type: '', message: '' })}
            className="mb-3 border-0 rounded-3"
          >
            <i className={`bi ${feedback.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
            {feedback.message}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Product Information Section */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-primary text-white border-0">
              <h6 className="mb-0 fw-bold">
                <i className="bi bi-box-seam me-2"></i>
                Product Information
              </h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Product Name <span className="text-danger">*</span>
                  </label>
                  <input 
                    className="form-control form-control-lg border-0 bg-light" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter product name"
                    style={{ borderRadius: '12px' }}
                    required
                  /> 
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Product Unit <span className="text-danger">*</span>
                  </label>
                  <select 
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    className="form-select form-select-lg border-0 bg-light"
                    style={{ borderRadius: '12px' }}
                    required
                  >
                    <option value="">Select unit...</option>
                    {unitData.map((item, index) => (
                      <option key={index} value={item.name}>{item.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          {/* Traders List Section */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-success text-white border-0 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-bold">
                <i className="bi bi-people me-2"></i>
                Assigned Traders
              </h6>
              <span className="badge bg-white text-success">
                {traderProducts.length} Trader{traderProducts.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="card-body p-0">
              {loadingTraders ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted">Loading traders...</p>
                </div>
              ) : traderProducts.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="fw-semibold">Trader</th>
                        <th className="fw-semibold">Quantity</th>
                        <th className="fw-semibold">Price</th>
                        <th className="fw-semibold">Status</th>
                        <th className="fw-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {traderProducts.map((trader, index) => (
                        <tr key={index} className={editedTraders[index]?.changed ? 'table-warning' : ''}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '40px', height: '40px'}}>
                                <span className="fw-bold text-primary">
                                  {trader.full_name?.charAt(0) || 'T'}
                                </span>
                              </div>
                              <div>
                                <div className="fw-semibold">{trader.full_name || 'N/A'}</div>
                                <small className="text-muted">{trader.email || 'No email'}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <input 
                              type="number" 
                              className="form-control border-0 bg-light"
                              value={editedTraders[index]?.quantity ?? trader.quantity ?? 0}
                              onChange={(e) => handleTraderInputChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                              style={{ borderRadius: '8px', maxWidth: '120px' }}
                            />
                          </td>
                          <td>
                            <div className="input-group" style={{ maxWidth: '150px' }}>
                              <input 
                                type="number" 
                                className="form-control border-0 bg-light"
                                value={editedTraders[index]?.selling_price ?? trader.selling_price ?? 0}
                                onChange={(e) => handleTraderInputChange(index, 'selling_price', parseFloat(e.target.value) || 0)}
                                min="0"
                                step="0.01"
                                style={{ borderRadius: '8px 0 0 8px' }}
                              />
                              <span className="input-group-text bg-light border-0" style={{ borderRadius: '0 8px 8px 0' }}>
                                {trader.currency_name || 'TSH'}
                              </span>
                            </div>
                          </td>
                          <td>
                            {editedTraders[index]?.changed ? (
                              <span className="badge bg-warning text-dark">
                                <i className="bi bi-pencil me-1"></i>
                                Modified
                              </span>
                            ) : (
                              <span className="badge bg-success">
                                <i className="bi bi-check-circle me-1"></i>
                                Saved
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <button 
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={() => handleUpdateTrader(index)}
                                disabled={saving || removingTrader === index || !editedTraders[index]?.changed}
                                title="Update trader"
                                style={{ borderRadius: '8px' }}
                              >
                                {saving ? (
                                  <Spinner size="sm" animation="border" />
                                ) : (
                                  <i className="bi bi-check-lg"></i>
                                )}
                              </button>
                              <button 
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => handleRemoveTrader(index)}
                                disabled={saving || removingTrader === index}
                                title="Remove trader"
                                style={{ borderRadius: '8px' }}
                              >
                                {removingTrader === index ? (
                                  <Spinner size="sm" animation="border" />
                                ) : (
                                  <i className="bi bi-trash"></i>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-people fs-1"></i>
                  <p className="mt-2">No traders assigned to this product</p>
                </div>
              )}
            </div>
          </div>
    
          {/* Action Buttons */}
          <div className="d-flex justify-content-end gap-3">
            <button 
              type="button"
              onClick={handleClose}
              className="btn btn-outline-secondary btn-lg px-4"
              style={{ borderRadius: '12px' }}
              disabled={saving}
            >
              <i className="bi bi-x-circle me-2"></i>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary btn-lg px-4"
              style={{ borderRadius: '12px' }}
              disabled={saving || loadingTraders}
            >
              {saving ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Updating...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  Update Product
                </>
              )}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default EditProductModal;